const GROUP_SEPARATOR = "\u001d";

/** Parsed GS1 application identifiers from a scanned element string or data matrix payload. */
export type Gs1ElementStringFields = {
  /** AI 01 — trade item GTIN (14 digits). */
  gtin: string | null;
  /** AI 240 — additional product id assigned by manufacturer (common MPN slot). */
  mpn: string | null;
  /** AI 241 — customer part number. */
  customerPartNumber: string | null;
  /** AI 10 — batch or lot number. */
  lot: string | null;
  /** AI 21 — serial number. */
  serial: string | null;
  /** AI 11 — production date (YYMMDD). */
  productionDate: string | null;
  /** AI 17 — expiration date (YYMMDD). */
  expirationDate: string | null;
  /** Full normalized scan after symbology prefixes are removed. */
  raw: string;
};

export type LabelScanFields = Gs1ElementStringFields & {
  /** ISO 15434 10D / 11D date code (often YYWW). */
  dateCode: string | null;
  trace: string | null;
  quantity: string | null;
};

type Gs1AiRule = {
  ai: string;
  fixedLength: number | null;
  field: keyof Omit<Gs1ElementStringFields, "raw">;
};

const GS1_AI_RULES: readonly Gs1AiRule[] = [
  { ai: "01", fixedLength: 14, field: "gtin" },
  { ai: "240", fixedLength: null, field: "mpn" },
  { ai: "241", fixedLength: null, field: "customerPartNumber" },
  { ai: "10", fixedLength: null, field: "lot" },
  { ai: "21", fixedLength: null, field: "serial" },
  { ai: "11", fixedLength: 6, field: "productionDate" },
  { ai: "17", fixedLength: 6, field: "expirationDate" },
];

type Iso15434Field = keyof Pick<
  LabelScanFields,
  "mpn" | "lot" | "serial" | "customerPartNumber" | "dateCode" | "productionDate" | "trace" | "quantity"
>;

const ISO15434_DI_FIELDS: ReadonlyArray<{ di: string; field: Iso15434Field | null }> = [
  { di: "10D", field: "dateCode" },
  { di: "11D", field: "dateCode" },
  { di: "12D", field: "dateCode" },
  { di: "13D", field: "dateCode" },
  { di: "14D", field: "dateCode" },
  { di: "15D", field: "dateCode" },
  { di: "16D", field: "dateCode" },
  { di: "17D", field: "dateCode" },
  { di: "9D", field: "productionDate" },
  { di: "1P", field: "mpn" },
  { di: "2P", field: null },
  { di: "1T", field: "trace" },
  { di: "1K", field: "customerPartNumber" },
  { di: "1S", field: "serial" },
  { di: "4L", field: "lot" },
  { di: "4S", field: "serial" },
  { di: "30P", field: "mpn" },
  { di: "32P", field: "mpn" },
  { di: "TT", field: "trace" },
  { di: "L1T", field: "trace" },
  { di: "LFP", field: "lot" },
  { di: "MTAI", field: "trace" },
  { di: "Q", field: "quantity" },
];

const ISO15434_DIS_DESC = [...ISO15434_DI_FIELDS]
  .map((entry) => entry.di)
  .sort((left, right) => right.length - left.length);

const ISO15434_DI_LOOKUP = new Map(
  ISO15434_DI_FIELDS.map((entry) => [entry.di.toUpperCase(), entry.field]),
);

function emptyGs1Fields(raw: string): Gs1ElementStringFields {
  return {
    gtin: null,
    mpn: null,
    customerPartNumber: null,
    lot: null,
    serial: null,
    productionDate: null,
    expirationDate: null,
    raw,
  };
}

function emptyLabelScanFields(raw: string): LabelScanFields {
  return {
    ...emptyGs1Fields(raw),
    dateCode: null,
    trace: null,
    quantity: null,
  };
}

/** Normalize scanner payload: strip trailing CR/LF, map RS/CR/LF to group separator. */
export function normalizeLabelScanInput(raw: string): string {
  let text = raw.trim();
  text = text.replace(/[\r\n]+$/gu, "");
  text = text.replace(/\]d2/giu, GROUP_SEPARATOR);
  text = text.replace(/\]C1/giu, "");
  text = text.replace(/^\]Q3/giu, "");
  text = text.replace(/^\]e0/giu, "");
  text = text.replace(/^\((1P|30P|32P)\)\s*/iu, "$1");
  text = text.replace(/^(?:MPN|MFR\s*P\/N|MFG\s*P\/N|P\/N|PART\s*(?:NO\.?|NUMBER|#))\s*[:#-]\s*/iu, "");
  text = text.replace(/^(?:1P|30P|32P)\s*[:#-]\s*/iu, "1P");
  text = text.replace(/\u001e/gu, GROUP_SEPARATOR);
  text = text.replace(/[\r\n]/gu, GROUP_SEPARATOR);
  text = text.replace(/\u001d+/gu, GROUP_SEPARATOR);
  text = text.replace(/\s+/gu, "");
  return text.trim();
}

function normalizePlainMpnInput(raw: string): string {
  return raw
    .trim()
    .replace(/[\r\n]+$/gu, "")
    .replace(/\]d2/giu, " ")
    .replace(/\]C1/giu, "")
    .replace(/^\]Q3/giu, "")
    .replace(/^\]e0/giu, "")
    .replace(/^\((1P|30P|32P)\)\s*/iu, "$1")
    .replace(/^(?:MPN|MFR\s*P\/N|MFG\s*P\/N|P\/N|PART\s*(?:NO\.?|NUMBER|#))\s*[:#-]\s*/iu, "")
    .replace(/^(?:1P|30P|32P)\s*[:#-]\s*/iu, "1P")
    .replace(/[\u001d\u001e\r\n]+/gu, " ")
    .replace(/\s+/gu, " ")
    .trim();
}

/** Strip scanner symbology prefixes and normalize GS1 group separators. */
export function normalizeGs1ElementString(raw: string): string {
  return normalizeLabelScanInput(raw);
}

function stripIso15434Header(text: string): { body: string; isIso: boolean } {
  const match = text.match(/^\[\)[>.]\u001e?06\u001d?/iu);
  if (match == null) {
    return { body: text, isIso: false };
  }
  return { body: text.slice(match[0].length), isIso: true };
}

function findIso15434Di(body: string, index: number): string | null {
  const slice = body.slice(index).toUpperCase();
  for (const di of ISO15434_DIS_DESC) {
    if (!slice.startsWith(di)) {
      continue;
    }
    const previous = body[index - 1] ?? "";
    if (di === "1T" && index > 0 && /\d/u.test(previous)) {
      const after = body[index + di.length] ?? "";
      if (after === "" || /\d/u.test(after)) {
        continue;
      }
    }
    if (di === "1P" && index > 0 && /\d/u.test(previous)) {
      continue;
    }
    if (di === "2P" && index > 0 && /\d/u.test(previous)) {
      continue;
    }
    if (di === "Q" && !isQuantityDataIdentifier(body, index)) {
      continue;
    }
    return di;
  }
  return null;
}

/** Q is quantity when it is followed by digits that end at the next data identifier. */
function isQuantityDataIdentifier(body: string, index: number): boolean {
  if (body.slice(index, index + 1).toUpperCase() !== "Q") {
    return false;
  }
  const digits = /^(\d+)/u.exec(body.slice(index + 1));
  if (digits == null) {
    return false;
  }
  let digitText = digits[1];
  let afterDigits = index + 1 + digitText.length;
  const afterDigitRun = body.slice(afterDigits, afterDigits + 1).toUpperCase();
  const afterLotMark = body[afterDigits + 1] ?? "";
  if (
    digitText.endsWith("1")
    && afterDigitRun === "T"
    && afterLotMark !== ""
    && !/\d/u.test(afterLotMark)
  ) {
    digitText = digitText.slice(0, -1);
    afterDigits -= 1;
  }
  const previous = body[index - 1] ?? "";
  if (index === 0 || !/[A-Z]/iu.test(previous)) {
    return digitText.length > 0;
  }
  if (afterDigits >= body.length) {
    return true;
  }
  return findIso15434Di(body, afterDigits) != null || isPPartFieldStart(body, afterDigits);
}

function isPPartFieldStart(body: string, index: number): boolean {
  if (body.slice(index, index + 1).toUpperCase() !== "P") {
    return false;
  }
  if (findIso15434Di(body, index) != null) {
    return false;
  }
  if (index === 0) {
    return true;
  }
  return /[0-9+]/u.test(body[index - 1] ?? "");
}

function diAtFieldStart(body: string, index: number): string | null {
  const di = findIso15434Di(body, index);
  if (di != null) {
    return di;
  }
  if (isPPartFieldStart(body, index)) {
    return "P";
  }
  return null;
}

function isIso15434FieldStart(body: string, index: number): boolean {
  return diAtFieldStart(body, index) != null;
}

function findNextIso15434FieldStart(body: string, from: number): number | null {
  let best: number | null = null;
  for (let index = from; index < body.length; index += 1) {
    if (!isIso15434FieldStart(body, index)) {
      continue;
    }
    if (best == null || index < best) {
      best = index;
    }
  }
  return best;
}

function findEarliestPatternIndex(rest: string, patterns: readonly RegExp[]): number {
  let earliest = rest.length;
  for (const pattern of patterns) {
    pattern.lastIndex = 0;
    const match = pattern.exec(rest);
    if (match?.index != null && match.index < earliest) {
      earliest = match.index;
    }
  }
  return earliest;
}

function readMpn1PValue(body: string, start: number): { value: string; nextIndex: number } {
  const rest = body.slice(start);
  const end = findEarliestPatternIndex(rest, [
    /10D\d/i,
    /11D\d/i,
    /9D\d/i,
    /Q\d/i,
    /4L/i,
    /TT/i,
    /1T/i,
    /4S/i,
    /MTAI/i,
    /30P/i,
    /32P/i,
    /1P/i,
    /L1T/i,
    /LFP/i,
  ]);
  const value = rest.slice(0, end);
  return { value, nextIndex: start + end };
}

function readMpnPValue(body: string, start: number): { value: string; nextIndex: number } {
  const nextField = findNextIso15434FieldStart(body, start);
  const rest = body.slice(start);
  const traceLike = findEarliestPatternIndex(rest, [/T\d{2,}/iu]);
  let end = nextField ?? body.length;
  const traceAt = start + traceLike;
  if (traceLike < rest.length && traceAt < end) {
    end = traceAt;
  }
  return { value: body.slice(start, end), nextIndex: end };
}

function readLot4LValue(body: string, start: number): { value: string; nextIndex: number } {
  return { value: body.slice(start), nextIndex: body.length };
}

function readIso15434Value(
  body: string,
  start: number,
  di: string,
): { value: string; nextIndex: number } {
  const upperDi = di.toUpperCase();
  if (upperDi === "1P" || upperDi === "32P" || upperDi === "30P") {
    return readMpn1PValue(body, start);
  }
  if (upperDi === "P") {
    return readMpnPValue(body, start);
  }
  if (upperDi === "4L" || upperDi === "LFP") {
    return readLot4LValue(body, start);
  }
  const next = findNextIso15434FieldStart(body, start);
  return { value: body.slice(start, next ?? body.length), nextIndex: next ?? body.length };
}

function assignIso15434Field(fields: LabelScanFields, field: Iso15434Field, value: string): void {
  const trimmed = value.trim();
  if (trimmed === "") {
    return;
  }
  if (field === "mpn" || field === "lot" || field === "serial" || field === "customerPartNumber") {
    fields[field] = trimmed;
    return;
  }
  if (field === "productionDate") {
    fields.productionDate = trimmed;
    return;
  }
  fields[field] = trimmed;
}

function parseIso15434Segment(fields: LabelScanFields, segment: string): void {
  const di = diAtFieldStart(segment, 0);
  if (di == null) {
    return;
  }
  const field = ISO15434_DI_LOOKUP.get(di.toUpperCase()) ?? (di.toUpperCase() === "P" ? "mpn" : null);
  if (field == null) {
    return;
  }
  assignIso15434Field(fields, field, segment.slice(di.length));
}

function segmentHasIso15434Di(segment: string): boolean {
  return findIso15434Di(segment, 0) != null || isPPartFieldStart(segment, 0);
}

function looksLikePlusDelimitedIso15434(text: string): boolean {
  if (!text.includes("+")) {
    return false;
  }
  const segments = text.split("+").filter((segment) => segment.trim() !== "");
  if (segments.length < 2) {
    return false;
  }
  return segments.slice(1).some((segment) => segmentHasIso15434Di(segment));
}

/** Supplier label scans that use plus as a field delimiter (common on reel labels). */
export function looksLikePlusDelimitedLabelScan(raw: string): boolean {
  return looksLikePlusDelimitedIso15434(normalizeLabelScanInput(raw));
}

function parsePlusDelimitedIso15434Body(body: string): LabelScanFields {
  const fields = emptyLabelScanFields(body);
  const segments = body.split("+").filter((segment) => segment.trim() !== "");
  if (segments.length === 0) {
    return fields;
  }

  const firstSegmentDi = diAtFieldStart(segments[0], 0);
  if (firstSegmentDi === "P") {
    assignIso15434Field(fields, "mpn", segments[0].slice(1));
    for (let index = 1; index < segments.length; index += 1) {
      parseIso15434Segment(fields, segments[index]);
    }
    return fields;
  }
  if (firstSegmentDi != null) {
    for (const segment of segments) {
      parseIso15434Segment(fields, segment);
    }
    return fields;
  }

  assignIso15434Field(fields, "mpn", segments[0]);
  for (let index = 1; index < segments.length; index += 1) {
    parseIso15434Segment(fields, segments[index]);
  }
  return fields;
}

function hasConcatenatedIso15434TrailingFields(body: string): boolean {
  return /(10D\d{4}|11D\d{4}|9D\d{4}|L1T|LFP|MTAI)/iu.test(body);
}

function parseIso15434Body(body: string, isIsoHeader = false): LabelScanFields {
  const fields = emptyLabelScanFields(body);
  if (looksLikePlusDelimitedIso15434(body)) {
    return parsePlusDelimitedIso15434Body(body);
  }
  if (body.includes(GROUP_SEPARATOR)) {
    for (const segment of body.split(GROUP_SEPARATOR)) {
      if (segment.trim() === "") continue;
      parseIso15434Segment(fields, segment);
    }
    return fields;
  }
  if (!isIsoHeader && !hasConcatenatedIso15434TrailingFields(body)) {
    const leadingMpnDi = /^(1P|30P|32P)/iu.exec(body)?.[1] ?? null;
    if (leadingMpnDi != null) {
      assignIso15434Field(fields, "mpn", body.slice(leadingMpnDi.length));
      return fields;
    }
  }

  let index = 0;
  while (index < body.length) {
    const di = diAtFieldStart(body, index);
    if (di == null) {
      index += 1;
      continue;
    }
    const field = ISO15434_DI_LOOKUP.get(di.toUpperCase()) ?? (di.toUpperCase() === "P" ? "mpn" : null);
    index += di.length;
    const { value, nextIndex } = readIso15434Value(body, index, di);
    if (field != null) {
      assignIso15434Field(fields, field, value);
    }
    index = nextIndex;
  }
  return fields;
}

function looksLikeIso15434Payload(text: string): boolean {
  if (/^\[\)[>.]/iu.test(text)) {
    return true;
  }
  if (looksLikePlusDelimitedIso15434(text)) {
    return true;
  }
  const upper = text.toUpperCase();
  if (text.includes(GROUP_SEPARATOR)) {
    return ISO15434_DIS_DESC.some((di) => upper.startsWith(di) || upper.includes(`${GROUP_SEPARATOR}${di}`));
  }
  return /^(1P|30P|32P).+/iu.test(upper);
}

function mergeLabelScanFields(base: LabelScanFields, overlay: LabelScanFields): LabelScanFields {
  return {
    raw: base.raw,
    gtin: overlay.gtin ?? base.gtin,
    mpn: overlay.mpn ?? base.mpn,
    customerPartNumber: overlay.customerPartNumber ?? base.customerPartNumber,
    lot: overlay.lot ?? base.lot,
    serial: overlay.serial ?? base.serial,
    productionDate: overlay.productionDate ?? base.productionDate,
    expirationDate: overlay.expirationDate ?? base.expirationDate,
    dateCode: overlay.dateCode ?? base.dateCode,
    trace: overlay.trace ?? base.trace,
    quantity: overlay.quantity ?? base.quantity,
  };
}

function parseParenthesizedGs1(text: string): Gs1ElementStringFields | null {
  if (!/\(\d{2,4}\)/u.test(text)) {
    return null;
  }
  const fields = emptyGs1Fields(text);
  const pattern = /\((\d{2,4})\)([^(\u001d]+)/gu;
  for (const match of text.matchAll(pattern)) {
    const ai = match[1];
    const value = match[2]?.trim() ?? "";
    if (value === "") continue;
    const rule = GS1_AI_RULES.find((entry) => entry.ai === ai);
    if (rule != null) {
      fields[rule.field] = value;
    }
  }
  return fields;
}

function readFixedLengthValue(text: string, start: number, length: number): string | null {
  const value = text.slice(start, start + length);
  return value.length === length ? value : null;
}

function readVariableLengthValue(text: string, start: number): { value: string; nextIndex: number } {
  const separatorIndex = text.indexOf(GROUP_SEPARATOR, start);
  if (separatorIndex >= 0) {
    return { value: text.slice(start, separatorIndex), nextIndex: separatorIndex + 1 };
  }
  return { value: text.slice(start), nextIndex: text.length };
}

function parseConcatenatedGs1(text: string): Gs1ElementStringFields {
  const fields = emptyGs1Fields(text);
  let index = 0;
  while (index < text.length) {
    if (text[index] === GROUP_SEPARATOR) {
      index += 1;
      continue;
    }
    const rule =
      GS1_AI_RULES.find((entry) => text.startsWith(entry.ai, index))
      ?? null;
    if (rule == null) {
      break;
    }
    index += rule.ai.length;
    if (rule.fixedLength != null) {
      const value = readFixedLengthValue(text, index, rule.fixedLength);
      if (value == null) {
        break;
      }
      fields[rule.field] = value;
      index += rule.fixedLength;
      continue;
    }
    const variable = readVariableLengthValue(text, index);
    fields[rule.field] = variable.value.trim();
    index = variable.nextIndex;
  }
  return fields;
}

function looksLikeConcatenatedGs1Payload(text: string): boolean {
  if (text.includes(GROUP_SEPARATOR)) {
    return true;
  }
  if (/^(240|241).+/u.test(text)) {
    return true;
  }
  return /^01\d{14}(240|241|10|21|11|17)/u.test(text);
}

function parseGs1FromNormalized(normalized: string): Gs1ElementStringFields {
  if (normalized === "") {
    return emptyGs1Fields("");
  }
  const parenthesized = parseParenthesizedGs1(normalized);
  if (parenthesized != null) {
    return parenthesized;
  }
  if (!looksLikeConcatenatedGs1Payload(normalized)) {
    return emptyGs1Fields(normalized);
  }
  return parseConcatenatedGs1(normalized);
}

/**
 * Parse a GS1 element string from a linear barcode or data matrix scan.
 * Supports parenthesized human-readable form and concatenated element strings with FNC1 separators.
 */
export function parseGs1ElementString(raw: string): Gs1ElementStringFields {
  return parseGs1FromNormalized(normalizeLabelScanInput(raw));
}

/** Parse ISO/IEC 15434 (ANSI MH10) and GS1 element strings from a scanner payload. */
export function parseLabelScanElementString(raw: string): LabelScanFields {
  const normalized = normalizeLabelScanInput(raw);
  if (normalized === "") {
    return emptyLabelScanFields("");
  }

  const { body, isIso } = stripIso15434Header(normalized);
  const fields = emptyLabelScanFields(normalized);

  if (isIso || looksLikeIso15434Payload(body)) {
    return mergeLabelScanFields(fields, parseIso15434Body(body, isIso));
  }

  const gs1 = parseGs1FromNormalized(normalized);
  return {
    ...fields,
    gtin: gs1.gtin,
    mpn: gs1.mpn,
    customerPartNumber: gs1.customerPartNumber,
    lot: gs1.lot,
    serial: gs1.serial,
    productionDate: gs1.productionDate,
    expirationDate: gs1.expirationDate,
  };
}

/** Best manufacturer part number from a label scan payload. */
export function resolveLabelScanManufacturerPartNumber(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return "";
  }
  const parsed = parseLabelScanElementString(trimmed);
  const candidates = [parsed.mpn, parsed.customerPartNumber, parsed.gtin];
  for (const candidate of candidates) {
    if (candidate != null && candidate.trim() !== "") {
      return candidate.trim();
    }
  }
  return normalizePlainMpnInput(trimmed);
}

/**
 * Candidate manufacturer part number tokens from a label scan or manual entry.
 * Preserves the exact resolved token first, then also includes prefix-stripped
 * variants for supplier labels that prepend a bare `P` or `1P`/`30P`/`32P`
 * data identifier without delimiters (so parts like `PBC03SAAN` and
 * `PSRF0703-100M` both resolve cleanly against AML).
 */
export function resolveGs1ManufacturerPartNumberCandidates(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (token: string | null | undefined): void => {
    const cleaned = (token ?? "").replace(/\s+/gu, " ").trim();
    if (cleaned.length < 2) return;
    const key = cleaned.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(cleaned);
  };

  const primary = resolveLabelScanManufacturerPartNumber(raw);
  push(primary);

  const normalizedPlain = normalizePlainMpnInput(raw);
  if (!raw.includes(GROUP_SEPARATOR) && !looksLikePlusDelimitedIso15434(normalizeLabelScanInput(raw))) {
    push(normalizedPlain);
  }

  for (const base of [...out]) {
    const diMatch = /^(1P|30P|32P|240)(.+)$/iu.exec(base);
    if (diMatch?.[2] != null) {
      push(diMatch[2]);
    }
    if (/^P[A-Z0-9]/iu.test(base) && base.length >= 4) {
      push(base.slice(1));
    }
  }

  return out;
}

/** Best manufacturer part number candidate from a GS1 or ISO 15434 scan. */
export function resolveGs1ManufacturerPartNumber(raw: string): string {
  return resolveLabelScanManufacturerPartNumber(raw);
}
